<script setup lang="ts">
import {computed, inject, ref, Ref, watch} from 'vue';
import OrderAdjustmentForm from '../OrderAdjustmentForm';
import Decimal from 'decimal.js';
import {
  LegacyAdjustmentCategory,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyOrderRow
} from '@hantera/portal-app';
import useBackend from '@/composition/useBackend';
import {v4 as uuidv4} from 'uuid';
import {ruleFieldId} from './order-row-constants';
import {NewRowAdjustment, newRowAdjustmentKey} from '../order-keys';

const props = defineProps< {
  orderRow: LegacyOrderRow
}>()

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const newRowAdjustment = inject(newRowAdjustmentKey, <Ref<NewRowAdjustment>><any>{});
const backend = useBackend();
const category = ref<LegacyAdjustmentCategory>();

const showForm = computed(() => {
  return newRowAdjustment.value?.rowId === props.orderRow.rowId;
});

watch(category, () => {
  if (newRowAdjustment.value) {
    newRowAdjustment.value.name = category.value?.name
  }
});

const showButton = computed(() => {
  return props.orderRow.quantity.sub(props.orderRow.returnedQuantity).sub(props.orderRow.lostQuantity).gt(0) || !props.orderRow.rowTotal.eq(0);
});

function openForm() {
  newRowAdjustment.value = {
    rowId: props.orderRow.rowId,
    value: {
      value: new Decimal(0),
      isPercentage: false,
    },
    name: category.value?.name,
  };
}

async function submit() {
  if (!newRowAdjustment.value || newRowAdjustment.value.value.value.eq(0)) {
    close();
    return;
  }
  if (!category.value) {
    // OrderAdjustmentForm ensures category is set
    return;
  }

  const adjustmentId = uuidv4();

  if (!order.value.includeVat) {
    // Turns out that accounting for VAT here causes more problems than it solves as legacy backend will mix things up / Kristoffer, 2025-03-29
    //priceWithVat = newRowAdjustment.value.value.mul(orderRow.vatPercentage.add(1));
  }
  
  await backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    `Add {name} ${newRowAdjustment.value.value.value.gt(new Decimal(0)) ? 'fee' : 'discount'} of {value} to product {productNumber}`,
    {
      value: newRowAdjustment.value.value.value + (newRowAdjustment.value.value.isPercentage ? '%' : ''),
      name: category.value.name,
      productNumber: props.orderRow.item.articleNumber,
    }
  )
    .command(legacyOrderCommands.addOrUpdateAdjustment, {
      adjustmentId: adjustmentId,
      percentage: newRowAdjustment.value.value.isPercentage ? newRowAdjustment.value.value.value.div(100) : undefined,
      priceWithVat: newRowAdjustment.value.value.isPercentage ? undefined : newRowAdjustment.value.value.value,
      affectedRows: [props.orderRow.rowId],
    })
    // TODO: Add Description and it's UI (if users miss it - otherwise we should drop this for now, maybe some kind of notes will be needed in the future but "Description" is a bit misleading)
    // .command(upsertRmaAdjustmentField, {
    //   rmaId: props.data.rmaId,
    //   rmaAdjustmentId: adjustmentId,
    //   fieldKey: 'Description',
    //   fieldValue: '',
    // })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: adjustmentId,
      fieldKey: ruleFieldId,
      fieldValue: true,
    })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: adjustmentId,
      fieldKey: 'CategoryCode',
      fieldValue: category.value.code,
    })
    .command(legacyOrderCommands.upsertAdjustmentField, {
      adjustmentId: adjustmentId,
      fieldKey: 'CategoryDescription',
      fieldValue: category.value.name,
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: `Created {name} ${newRowAdjustment.value.value.value.gt(new Decimal(0)) ? 'fee' : 'discount'} of {value} to product {productNumber}`,
      fields: {
        value: newRowAdjustment.value.value.value + (newRowAdjustment.value.value.isPercentage ? '%' : ''),
        name: category.value.name,
        productNumber: props.orderRow.item.articleNumber,
      }
    })
    .send();

  close();
  return;
}
function close() {
  newRowAdjustment.value = undefined;
}
</script>

<template>
  <div
    class="legacy-order-row-add-adjustment"
    v-if="showButton"
  >
    <button
      class="btn-editable"
      v-if="!showForm"
      @click="openForm"
    >Add fee/discount</button>
    <OrderAdjustmentForm
      v-if="showForm && newRowAdjustment"
      v-model:selected-category="category"
      v-model:value="newRowAdjustment.value"
      @submit="submit"
      @cancel="close"
    />
  </div>
</template>

<style>
.legacy-order-row-add-adjustment {
  grid-column: 1 / 5;
}

.legacy-order-row-add-adjustment .btn-editable:hover {
  color: hsl(var(--color-text));
}
</style>